import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Divider,
  Avatar,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReportsIcon from '@mui/icons-material/BarChart';
import AddIcon from '@mui/icons-material/AddCircle';
import LogoutIcon from '@mui/icons-material/ExitToApp';

import FarmIcon from '@mui/icons-material/Agriculture';
import HomeIcon from '@mui/icons-material/Home';

const Header: React.FC = () => {
  const { user, signOut } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const menuItems = [
    { text: 'Home', path: '/', icon: <HomeIcon /> },
    { text: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
    { text: 'Data Entry', path: '/data-entry', icon: <AddIcon /> },
    { text: 'Reports', path: '/reports', icon: <ReportsIcon /> },
    { text: 'Farms', path: '/farms', icon: <FarmIcon /> },
  ];

  return (
    <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'white', color: 'text.primary' }}>
      <Toolbar sx={{ justifyContent: 'space-between', maxWidth: '1280px', width: '100%', margin: '0 auto' }}>
        {/* Logo/Brand */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            🍓 Strawberry Manual Tracker
          </Typography>
        </Box>

        {user ? (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', mr: 2 }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.path}
                    component={RouterLink}
                    to={item.path}
                    color="inherit"
                    startIcon={item.icon}
                    sx={{
                      mx: 1,
                      color: 'text.primary',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                size="large"
                aria-label="menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* User Profile */}
            <Tooltip title={user.email}>
              <IconButton
                onClick={handleMenu}
                size="small"
                sx={{ ml: 2 }}
                aria-controls="user-menu"
                aria-haspopup="true"
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                  {user.email ? user.email[0].toUpperCase() : 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>

            {/* Mobile Menu */}
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl) && isMobile}
              onClose={handleClose}
            >
              {menuItems.map((item) => (
                <MenuItem
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  onClick={handleClose}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ mr: 1, display: 'flex' }}>{item.icon}</Box>
                    {item.text}
                  </Box>
                </MenuItem>
              ))}
              <Divider />
              <MenuItem onClick={handleLogout}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LogoutIcon sx={{ mr: 1 }} />
                  Logout
                </Box>
              </MenuItem>
            </Menu>

            {/* Desktop User Menu */}
            <Menu
              id="user-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && !isMobile}
              onClose={handleClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                  mt: 1.5,
                  '& .MuiAvatar-root': {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleClose} component={RouterLink} to="/profile">
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {user.email ? user.email[0].toUpperCase() : 'U'}
                </Avatar>
                Profile
              </MenuItem>
              <Button
                color="inherit"
                component={RouterLink}
                to="/dashboard"
                startIcon={<DashboardIcon />}
                sx={{ mr: 1 }}
              >
                Dashboard
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/data-entry"
                startIcon={<AddIcon />}
                sx={{ mr: 1 }}
              >
                New Entry
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/reports"
                startIcon={<ReportsIcon />}
                sx={{ mr: 1 }}
              >
                Reports
              </Button>
              <Button
                color="inherit"
                component={RouterLink}
                to="/farms"
                startIcon={<FarmIcon />}
              >
                Farms
              </Button>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          <Box>
            <Button
              component={RouterLink}
              to="/login"
              color="inherit"
              sx={{ mr: 1 }}
            >
              Login
            </Button>
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              color="primary"
              size={isMobile ? 'small' : 'medium'}
            >
              Sign Up
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
